import i18n from "@/i18n";
import { detailOr } from "@/lib/apiErrorMessage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
    Camera, Image as ImageIcon, FileText, Upload, Check, ScanLine,
    X as XIcon, RefreshCw, WifiOff, AlertTriangle, Pencil, MapPin,
} from "lucide-react";

/**
 * Companion — standalone phone capture page.
 *
 * Loaded OUTSIDE the main MiseOne Layout / AppContext so it stays tiny and
 * loads in <300 ms. Expects `?t=<session-id>` query param.
 *
 * P0-B stability fixes (Feb 2026):
 *   - Heartbeat is a recursive setTimeout (not setInterval) that fires
 *     IMMEDIATELY on `visibilitychange → visible`, so a locked/backgrounded
 *     phone reconnects the moment the user reopens the tab.
 *   - The backend now extends `expires_at` on every heartbeat/submit, so
 *     an active session never expires mid-flow (see companion_service.py).
 *   - Submits are wrapped in `withRetry()` — 3 attempts with exponential
 *     backoff so a transient network switch (Wi-Fi ↔ cellular) does not
 *     drop a scan or a photo silently.
 *   - Barcode mode restarts the detector loop when the tab becomes
 *     visible again (camera track gets paused by iOS when backgrounded).
 *   - Same barcode can be rescanned after 1s.
 *   - Explicit UI states: Connecting / Connected / Reconnecting / Offline
 *     / Session Expired.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });

// Heartbeat cadence — server treats a session as disconnected only after
// 45 s of silence (HEARTBEAT_WINDOW_SECONDS), so 8 s gives us ~5 tries.
const HEARTBEAT_MS = 8_000;
// Max number of consecutive heartbeat failures before we surface "Offline".
const OFFLINE_AFTER_MISSES = 2;

async function withRetry(fn, { tries = 3, baseMs = 400 } = {}) {
    let lastErr;
    for (let i = 0; i < tries; i++) {
        try { return await fn(); }
        catch (e) {
            lastErr = e;
            // Never retry a definite server rejection.
            const s = e?.response?.status;
            if (s && s !== 408 && s !== 429 && s < 500) throw e;
            if (i < tries - 1) {
                const jitter = Math.floor(Math.random() * 150);
                await new Promise((r) => setTimeout(r, baseMs * (2 ** i) + jitter));
            }
        }
    }
    throw lastErr;
}

export default function Companion() {
    const [params] = useSearchParams();
    const token = params.get("t") || "";
    // status: checking | ready | reconnecting | offline | expired | error
    const [status, setStatus] = useState("checking");
    const [mode, setMode] = useState(null);
    const [ctx, setCtx] = useState({ restaurant_name: "", user_name: "", events_count: 0 });
    const [error, setError] = useState("");

    const missesRef = useRef(0);
    const timerRef = useRef(null);
    const cancelledRef = useRef(false);

    const scheduleHeartbeat = useCallback((delay = HEARTBEAT_MS) => {
        if (cancelledRef.current) return;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            if (cancelledRef.current) return;
            try {
                const { data } = await api.post(
                    "/companion/heartbeat", null, { params: { t: token }, timeout: 10_000 });
                missesRef.current = 0;
                if (typeof data?.events_count === "number") {
                    setCtx((c) => ({ ...c, events_count: data.events_count }));
                }
                setStatus((s) => (s === "reconnecting" || s === "offline" ? "ready" : s));
            } catch (err) {
                const s = err?.response?.status;
                if (s === 410 || s === 404) {
                    setStatus((cur) => (cur === "finished" ? cur : "expired"));
                    setError(detailOr(err?.response?.data?.detail, i18n.t("ui.companion.sessionEnded", "Session ended")));
                    return;                       // stop the loop
                }
                missesRef.current += 1;
                if (missesRef.current >= OFFLINE_AFTER_MISSES) {
                    setStatus((cur) => (cur === "expired" ? cur : "offline"));
                } else {
                    setStatus((cur) => (cur === "expired" ? cur : "reconnecting"));
                }
            } finally {
                scheduleHeartbeat();
            }
        }, delay);
    }, [token]);

    // Immediate reconnect when the tab becomes visible again (phone
    // unlock / return from another app). setInterval / setTimeout are
    // throttled to 1/minute in background on mobile, so we bypass the
    // scheduled tick and probe the server right away.
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState !== "visible") return;
            missesRef.current = 0;
            setStatus((s) => (s === "expired" || s === "error" || s === "finished" ? s : "reconnecting"));
            scheduleHeartbeat(0);
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [scheduleHeartbeat]);

    // Boot: validate token + start heartbeat loop.
    useEffect(() => {
        cancelledRef.current = false;
        if (!token) { setStatus("error"); setError(i18n.t("ui.companion.sessionLinkInvalidOrExpired", "Session link invalid or expired")); return; }
        (async () => {
            try {
                const { data } = await withRetry(
                    () => api.get("/companion/session-info", { params: { t: token }, timeout: 10_000 }),
                    { tries: 3 });
                if (cancelledRef.current) return;
                setMode(data.mode);
                setCtx({
                    restaurant_name: data.restaurant_name || "",
                    user_name: data.user_name || "",
                    events_count: data.events_count || 0,
                });
                setStatus("ready");
            } catch (err) {
                if (cancelledRef.current) return;
                const s = err.response?.status;
                setStatus(s === 410 ? "expired" : "error");
                setError(detailOr(err.response?.data?.detail, i18n.t("ui.companion.sessionError", "Session error")));
                return;
            }
            scheduleHeartbeat();
        })();
        return () => {
            cancelledRef.current = true;
            clearTimeout(timerRef.current);
        };
    }, [token, scheduleHeartbeat]);

    const captureLabel = mode === "delivery"
        ? i18n.t("ui.companion.deliveryNoteCapture", "Delivery Note Capture")
        : mode === "barcode" ? i18n.t("ui.companion.inventoryScanner", "Inventory Scanner") : i18n.t("ui.companion.companion", "Companion");
    const bumpCount = () => setCtx((c) => ({ ...c, events_count: (c.events_count || 0) + 1 }));

    return (
        <div className={`min-h-screen text-neutral-900 ${mode === "barcode" ? "bg-[#f7f5f1]" : "bg-neutral-50"}`} data-testid="companion-page">
            <header className={`px-4 py-3 border-b border-neutral-200 sticky top-0 z-10 ${mode === "barcode" ? "bg-[#f7f5f1]/95 backdrop-blur" : "bg-white"}`}>
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{i18n.t("ui.companion.miseone", "MiseOne")}</div>
                        <div className="text-sm font-semibold truncate" data-testid="companion-title">
                            {captureLabel}
                        </div>
                        {ctx.restaurant_name && (
                            <div className="text-xs text-neutral-600 truncate flex items-center gap-1" data-testid="companion-restaurant">
                                {mode === "barcode" && <MapPin className="h-3 w-3 shrink-0" />}
                                {ctx.restaurant_name}
                            </div>
                        )}
                    </div>
                    <div className="shrink-0 text-right flex flex-col items-end gap-1">
                        <ConnectionPill status={status} />
                        {status !== "checking" && status !== "error" && mode !== "barcode" && (
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-neutral-400">{i18n.t("ui.companion.uploaded", "Uploaded")}</div>
                                <div className="text-sm font-bold tabular-nums" data-testid="companion-upload-count">
                                    {ctx.events_count}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-md mx-auto p-4">
                {status === "checking" && <p className="text-sm text-neutral-500 py-10 text-center">{i18n.t("ui.companion.connecting", "Connecting…")}</p>}
                {status === "expired" && (
                    <div className="py-10 text-center text-sm" data-testid="companion-expired">
                        <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                        <div className="text-lg font-semibold mb-2">{i18n.t("ui.companion.sessionExpired", "Session expired")}</div>
                        <div className="text-neutral-500">{i18n.t("ui.companion.returnToTheDesktopAndStart", "Return to the desktop and start a new session.")}</div>
                    </div>
                )}
                {status === "error" && (
                    <div className="py-10 text-center text-sm text-red-600" data-testid="companion-boot-error">
                        <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
                        {error || i18n.t("ui.companion.sessionError", "Session error")}
                    </div>
                )}
                {status === "finished" && (
                    <div className="py-10 text-center text-sm" data-testid="phone-capture-finished">
                        <Check className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
                        <div className="text-lg font-semibold mb-2">{i18n.t("ui.companion.captureFinished", "Pages sent")}</div>
                        <div className="text-neutral-500">{i18n.t("ui.companion.continueOnDesktop", "Check the pages on the desktop and press Read pages.")}</div>
                    </div>
                )}
                {(status === "ready" || status === "reconnecting" || status === "offline") && mode === "delivery" && (
                    <DeliveryMode token={token} onUploaded={bumpCount} online={status !== "offline"}
                        onFinished={() => setStatus("finished")} />
                )}
                {(status === "ready" || status === "reconnecting" || status === "offline") && mode === "barcode"  && (
                    <BarcodeMode token={token} onSent={bumpCount} online={status !== "offline"} />
                )}
            </main>
        </div>
    );
}

function ConnectionPill({ status }) {
    const map = {
        checking:     { cls: "bg-neutral-100 text-neutral-700 border-neutral-200", label: i18n.t("ui.companion.connecting", "Connecting…") },
        ready:        { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: i18n.t("ui.companion.connected", "Connected"), Icon: Check },
        reconnecting: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: i18n.t("ui.companion.reconnecting", "Reconnecting…"), Icon: RefreshCw },
        offline:      { cls: "bg-amber-50 text-amber-800 border-amber-200", label: i18n.t("ui.companion.offline", "Offline"), Icon: WifiOff },
        expired:      { cls: "bg-red-50 text-red-800 border-red-200", label: i18n.t("ui.companion.sessionExpired", "Session Expired"), Icon: AlertTriangle },
        error:        { cls: "bg-red-50 text-red-800 border-red-200", label: i18n.t("ui.companion.error", "Error"), Icon: AlertTriangle },
    };
    const m = map[status] || map.checking;
    const Icon = m.Icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${m.cls}`}
              data-testid={`companion-conn-${status}`}>
            {Icon && <Icon className={`h-3 w-3 ${status === "reconnecting" ? "animate-spin" : ""}`} />}
            {m.label}
        </span>
    );
}

// -------------------- Delivery mode --------------------
function DeliveryMode({ token, onUploaded, online, onFinished }) {
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);
    const pdfRef = useRef(null);
    const [file, setFile] = useState(null);
    const [phase, setPhase] = useState("idle");     // idle | uploading | processing | done | error
    const [progress, setProgress] = useState(0);    // 0..100
    const [err, setErr] = useState("");

    const acceptFile = (f) => {
        setErr("");
        if (!f) return;
        const okType = /^(image\/(jpeg|png|jpg)|application\/pdf)$/i.test(f.type)
            || /\.(jpe?g|png|pdf)$/i.test(f.name);
        if (!okType) { setErr(i18n.t("ui.companion.onlyJpgPngOrPdfAre", "Only JPG, PNG or PDF are allowed")); return; }
        if (f.size > 20 * 1024 * 1024) { setErr(i18n.t("ui.companion.fileTooLargeMax20Mb", "File too large (max 20 MB)")); return; }
        setFile(f);
        setPhase("idle");
        setProgress(0);
    };

    const upload = async () => {
        if (!file) return;
        setErr("");
        setPhase("uploading");
        setProgress(0);
        try {
            await withRetry(async () => {
                const form = new FormData();
                form.append("file", file);
                await api.post("/companion/submit-delivery", form, {
                    params: { t: token },
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 120_000,
                    onUploadProgress: (evt) => {
                        if (evt.total) {
                            const pct = Math.round((evt.loaded / evt.total) * 90); // reserve 10% for AI
                            setProgress(pct);
                        }
                    },
                });
            }, { tries: 3, baseMs: 800 });
            setProgress(95);
            setPhase("processing");
            // The AI extract runs server-side and the response returns
            // AFTER extraction — by the time withRetry() resolves, the
            // whole thing is done. Show the "processing" state briefly
            // so the user sees the two-step feedback.
            setTimeout(() => {
                setProgress(100);
                setPhase("done");
                if (onUploaded) onUploaded();
            }, 300);
        } catch (e) {
            const s = e.response?.status;
            let msg;
            if (s === 410) msg = i18n.t("ui.companion.sessionExpiredReturnToTheDesktop", "Session expired — return to the desktop to start a new one.");
            else if (s === 413) msg = i18n.t("ui.companion.fileIsTooLargeMax20", "File is too large (max 20 MB).");
            else if (s === 415) msg = i18n.t("ui.companion.onlyJpgPngOrPdfAre", "Only JPG, PNG or PDF are supported.");
            else if (s === 429) msg = i18n.t("ui.companion.tooManyUploadsPleaseWaitA", "Too many uploads — please wait a few seconds.");
            else if (s === 502) msg = detailOr(e.response?.data?.detail, i18n.t("ui.companion.aiCouldNotReadThisPhoto", "AI could not read this photo."));
            else if (!online) msg = i18n.t("ui.companion.offlineCheckYourPhoneSConnection", "Offline — check your phone's connection and try again.");
            else msg = detailOr(e.response?.data?.detail, i18n.t("ui.companion.uploadFailed", "Upload failed"));
            setErr(msg);
            setPhase("error");
        }
    };

    const finishCapture = async () => {
        setErr("");
        try {
            await api.post("/companion/phone-finish", null, { params: { t: token }, timeout: 15_000 });
            onFinished();
        } catch (e) {
            if (e.response?.status === 410) { onFinished(); return; }
            setErr(detailOr(e.response?.data?.detail, i18n.t("ui.companion.finishFailed", "Could not finish — check the connection and try again.")));
        }
    };

    const uploaded = phase === "done";
    const busy = phase === "uploading" || phase === "processing";

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
                <BigButton icon={<Camera className="h-5 w-5" />} label={i18n.t("ui.companion.camera", "Camera")}
                    onClick={() => cameraRef.current?.click()} testId="phone-camera" />
                <BigButton icon={<ImageIcon className="h-5 w-5" />} label={i18n.t("ui.companion.gallery", "Gallery")}
                    onClick={() => galleryRef.current?.click()} testId="phone-gallery" />
                <BigButton icon={<FileText className="h-5 w-5" />} label={i18n.t("ui.companion.pdf", "PDF")}
                    onClick={() => pdfRef.current?.click()} testId="phone-pdf" />
            </div>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }} />
            <input ref={galleryRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }} />
            <input ref={pdfRef} type="file" accept="application/pdf" className="hidden"
                onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }} />

            {file && !uploaded && (
                <div className="rounded-md border border-neutral-200 bg-white p-3 flex items-center gap-3"
                     data-testid="phone-file-preview">
                    <FileText className="h-5 w-5 text-neutral-500" />
                    <div className="flex-1 text-sm">
                        <div className="font-semibold truncate">{file.name}</div>
                        <div className="text-xs text-neutral-500">{(file.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <button onClick={() => { setFile(null); setPhase("idle"); setProgress(0); }}
                            className="h-8 w-8 grid place-items-center rounded hover:bg-neutral-100"
                            data-testid="phone-clear-file">
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {busy && (
                <div className="rounded-md border border-neutral-200 bg-white p-3 space-y-2"
                     data-testid="phone-upload-progress">
                    <div className="flex items-center justify-between text-xs font-semibold text-neutral-700">
                        <span>{phase === "uploading" ? i18n.t("ui.companion.uploadingPhoto", "Uploading photo…") : i18n.t("ui.companion.savingPage", "Saving page…")}</span>
                        <span className="tabular-nums" data-testid="phone-upload-pct">{progress}%</span>
                    </div>
                    <div className="h-2 rounded bg-neutral-100 overflow-hidden">
                        <div className="h-full bg-neutral-900 transition-all duration-200"
                             style={{ width: `${progress}%` }} />
                    </div>
                </div>
            )}

            {uploaded && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2"
                     data-testid="phone-upload-success">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                        <Check className="h-5 w-5" />
                        <span>{i18n.t("ui.companion.uploaded", "Uploaded ✓")}</span>
                    </div>
                    <button
                        onClick={() => { setFile(null); setPhase("idle"); setProgress(0); }}
                        data-testid="phone-take-another-btn"
                        className="w-full h-11 rounded-md bg-white border border-emerald-300 text-emerald-800 text-sm font-semibold inline-flex items-center justify-center gap-2">
                        <Camera className="h-4 w-4" /> Take another photo
                    </button>
                </div>
            )}

            {err && <div className="text-sm text-red-600" data-testid="phone-error">{err}</div>}
            {!online && !err && (
                <div className="text-sm text-amber-700 flex items-center gap-1" data-testid="phone-offline-hint">
                    <WifiOff className="h-3.5 w-3.5" /> Waiting for connection…
                </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-2">
                <button disabled={!file || busy || uploaded} onClick={upload} data-testid="phone-upload-btn"
                    className="h-12 rounded-md bg-neutral-900 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Upload className="h-4 w-4" /> {busy ? (phase === "uploading" ? i18n.t("ui.companion.uploading", "Uploading…") : i18n.t("ui.companion.savingPage", "Saving page…")) : i18n.t("ui.companion.upload", "Upload")}
                </button>
                <button onClick={finishCapture} disabled={busy || (!!file && !uploaded)} data-testid="phone-done-btn"
                    className="h-12 rounded-md border border-neutral-300 bg-white font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Check className="h-4 w-4" /> {i18n.t("ui.companion.finish", "Finish")}
                </button>
            </div>
        </div>
    );
}

// -------------------- Barcode mode --------------------
const newKey = () => ((typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const freshKeys = () => ({ save: newKey(), replace: newKey(), add: newKey() });
const tc = (k, d, o) => i18n.t(`ui.companion.${k}`, { defaultValue: d, ...(o || {}) });

function BarcodeMode({ token, onSent, online }) {
    const [manual, setManual] = useState("");
    const [draft, setDraft] = useState(null);
    const [quantity, setQuantity] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");
    const [list, setList] = useState({ items: [], scans: [] });
    const [detectorSupported, setDetectorSupported] = useState(null);
    const [scanning, setScanning] = useState(false);
    const keysRef = useRef(freshKeys());
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const detectorRef = useRef(null);
    const rafRef = useRef(null);
    const qtyRef = useRef(null);
    const lastDetectRef = useRef({ code: "", at: 0 });
    const lookupRef = useRef(null);

    const loadList = useCallback(async () => {
        try {
            const { data } = await api.get("/companion/count-items", { params: { t: token }, timeout: 10_000 });
            setList({ items: data.items || [], scans: data.scans || [], writable: data.writable });
        } catch (_e) { /* keep last confirmed list */ }
    }, [token]);
    useEffect(() => {
        loadList();
        const id = setInterval(loadList, 10_000);
        return () => clearInterval(id);
    }, [loadList]);

    const lookup = useCallback(async (code) => {
        const v = (code || "").trim();
        if (!v) return;
        setErr(""); setResult(null);
        try {
            const { data } = await withRetry(() => api.get("/companion/lookup",
                { params: { t: token, barcode: v }, timeout: 10_000 }), { tries: 2 });
            keysRef.current = freshKeys();
            setDraft(data);
            setQuantity("");
            setManual(v);
            setTimeout(() => qtyRef.current?.focus(), 100);
        } catch (e) {
            setErr(detailOr(e.response?.data?.detail, tc("lookupFailed", "Lookup failed — try again")));
        }
    }, [token]);
    lookupRef.current = lookup;

    useEffect(() => {
        setDetectorSupported(typeof window !== "undefined" && "BarcodeDetector" in window);
    }, []);

    const stopCamera = useCallback(() => {
        setScanning(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((tr) => tr.stop());
            streamRef.current = null;
        }
    }, []);

    const loopDetect = useCallback(async () => {
        if (!detectorRef.current || !videoRef.current) return;
        try {
            if (videoRef.current.readyState >= 2 && !videoRef.current.paused) {
                const codes = await detectorRef.current.detect(videoRef.current);
                const found = codes?.[0]?.rawValue;
                const now = Date.now();
                const { code: lastCode, at: lastAt } = lastDetectRef.current;
                if (found && (found !== lastCode || now - lastAt > 1_200)) {
                    lastDetectRef.current = { code: found, at: now };
                    lookupRef.current?.(found);
                }
            }
        } catch (_e) { /* transient */ }
        if (streamRef.current) rafRef.current = requestAnimationFrame(loopDetect);
    }, []);

    const startCamera = useCallback(async () => {
        setErr("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } }, audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            if (detectorSupported) {
                detectorRef.current = new window.BarcodeDetector({
                    formats: ["ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "qr_code"],
                });
                setScanning(true);
                loopDetect();
            }
        } catch (_e) {
            setErr(i18n.t("ui.companion.cameraDeniedOrUnavailable", "Camera denied or unavailable"));
        }
    }, [detectorSupported, loopDetect]);

    useEffect(() => {
        const onVis = async () => {
            if (document.visibilityState !== "visible" || !streamRef.current) return;
            if (videoRef.current?.paused) {
                try { await videoRef.current.play(); } catch (_e) { /* ignore */ }
            }
            if (detectorRef.current && !rafRef.current) rafRef.current = requestAnimationFrame(loopDetect);
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [loopDetect]);
    useEffect(() => () => stopCamera(), [stopCamera]);

    const onQty = (v) => {
        setQuantity(v);
        if (!busy) keysRef.current = freshKeys();
    };

    const submit = async (intent = null) => {
        if (busy || !draft) return;
        setErr("");
        const qty = Number(quantity);
        if (!(qty > 0)) { setErr(i18n.t("ui.companion.enterAPositiveQuantity", "Enter a positive quantity")); return; }
        setBusy(true);
        const key = keysRef.current[intent || "save"];
        try {
            const body = { barcode: draft.barcode, quantity: qty, idempotency_key: key };
            if (intent) body.intent = intent;
            if (intent === "replace" && draft.already_counted) body.expected_quantity = draft.already_counted.quantity;
            const { data } = await withRetry(() => api.post("/companion/submit-barcode", body,
                { params: { t: token }, timeout: 15_000 }), { tries: 3, baseMs: 500 });
            setResult({ ...data, quantity: qty, product_name: draft.product?.product_name, barcode: draft.barcode });
            if (data.status === "already_counted" || data.status === "conflict") {
                setDraft((d) => ({ ...d, already_counted: {
                    id: data.duplicate?.existing_item_id, quantity: data.duplicate?.current_quantity,
                    unit: data.duplicate?.unit } }));
                keysRef.current = freshKeys();
            } else if (data.status === "saved" || data.status === "pending") {
                if (onSent) onSent();
                setDraft(null); setQuantity(""); setManual("");
                keysRef.current = freshKeys();
                lastDetectRef.current = { code: "", at: 0 };
            }
        } catch (e) {
            const s = e.response?.status;
            let msg;
            if (s === 410) msg = i18n.t("ui.companion.sessionExpiredReturnToTheDesktop", "Session expired — return to the desktop to start a new one.");
            else if (s === 429) msg = i18n.t("ui.companion.tooManyScansPauseACouple", "Too many scans — pause a couple of seconds.");
            else if (!online && !s) msg = i18n.t("ui.companion.offlineCheckYourPhoneSConnection", "Offline — check your phone's connection and try again.");
            else msg = detailOr(e.response?.data?.detail, tc("notSavedTryAgain", "Not saved — try again"));
            setResult({ status: "failed", error: msg, quantity: qty, product_name: draft.product?.product_name, barcode: draft.barcode });
        } finally {
            setBusy(false);
            loadList();
        }
    };

    const nextScan = () => {
        setDraft(null); setQuantity(""); setManual(""); setResult(null); setErr("");
        keysRef.current = freshKeys();
        lastDetectRef.current = { code: "", at: 0 };
    };
    const finish = () => { stopCamera(); nextScan(); window.close(); };

    return (
        <div className="space-y-3" data-testid="phone-scanner">
            <ScanView videoRef={videoRef} scanning={scanning} onStart={startCamera} />
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(manual); }}>
                <input value={manual} onChange={(e) => setManual(e.target.value)} inputMode="numeric"
                    placeholder={i18n.t("ui.companion.barcode", "Barcode")} data-testid="phone-barcode-input"
                    className="flex-1 min-w-0 h-11 px-3 rounded-lg border border-neutral-300 bg-white font-mono text-sm" />
                <button type="submit" disabled={!manual.trim()} data-testid="phone-find-btn"
                    className="h-11 px-4 rounded-lg border border-neutral-300 bg-white text-sm font-semibold disabled:opacity-50">
                    {tc("find", "Find")}
                </button>
            </form>
            {draft && <DraftCard draft={draft} quantity={quantity} onQty={onQty} qtyRef={qtyRef} />}
            <ResultBanner result={result} />
            {err && <div className="text-sm text-red-700" data-testid="phone-error">{err}</div>}
            {!online && (
                <div className="text-sm text-amber-700 flex items-center gap-1" data-testid="phone-offline-hint">
                    <WifiOff className="h-3.5 w-3.5" /> {i18n.t("ui.companion.waitingForConnection", "Waiting for connection…")}
                </div>
            )}
            <ScanActions draft={draft} busy={busy} quantity={quantity} onSubmit={submit} onNext={nextScan} onFinish={finish} />
            <CountedList list={list} onCorrect={(it) => lookup(it.barcode)} />
        </div>
    );
}

function ScanView({ videoRef, scanning, onStart }) {
    const c = "absolute w-6 h-6 border-white/90";
    return (
        <div className="rounded-xl overflow-hidden bg-neutral-900 h-40 relative" data-testid="phone-scan-view">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <div className="pointer-events-none absolute inset-x-10 inset-y-5">
                <span className={`${c} left-0 top-0 border-l-2 border-t-2 rounded-tl`} />
                <span className={`${c} right-0 top-0 border-r-2 border-t-2 rounded-tr`} />
                <span className={`${c} left-0 bottom-0 border-l-2 border-b-2 rounded-bl`} />
                <span className={`${c} right-0 bottom-0 border-r-2 border-b-2 rounded-br`} />
            </div>
            {!scanning && (
                <button onClick={onStart} data-testid="phone-start-camera"
                    className="absolute inset-0 grid place-items-center bg-black/60 text-white text-sm font-semibold">
                    <span className="flex flex-col items-center gap-1.5">
                        <ScanLine className="h-6 w-6" />
                        {i18n.t("ui.companion.tapToStartCamera", "Tap to start camera")}
                    </span>
                </button>
            )}
        </div>
    );
}

function DraftCard({ draft, quantity, onQty, qtyRef }) {
    const p = draft.product;
    const known = draft.matched === "internal" && p;
    const unit = known ? p.counting_unit : "";
    const head = known
        ? { cls: "text-emerald-800", Icon: Check, label: tc("productIdentified", "Product identified") }
        : { cls: "text-amber-800", Icon: AlertTriangle, label: draft.matched === "ambiguous"
            ? tc("barcodeAmbiguous", "Several products use this barcode")
            : tc("notInCatalogue", "Not in the product catalogue") };
    return (
        <div className="rounded-xl border border-neutral-200 bg-white divide-y divide-neutral-100" data-testid="phone-draft-card">
            <div className="p-4">
                <div className={`flex items-center gap-1.5 text-xs font-semibold ${head.cls}`} data-testid="phone-draft-status">
                    <head.Icon className="h-4 w-4" /> {head.label}
                </div>
                {known && <div className="mt-2 text-base font-semibold" data-testid="phone-product-name">{p.product_name}</div>}
                <div className="mt-1 text-xs text-neutral-600 space-y-0.5">
                    <div>{i18n.t("ui.companion.barcode", "Barcode")} <span className="font-mono" data-testid="phone-product-barcode">{draft.barcode}</span></div>
                    {known && <div data-testid="phone-counting-unit">{tc("countingUnit", "Counting unit")}: <span className="font-semibold text-neutral-800">{unit}</span></div>}
                    {known && p.pack_size && p.pack_size_unit && (
                        <div data-testid="phone-pack-size">{tc("packSize", "Pack size")}: {p.pack_size} {p.pack_size_unit}</div>
                    )}
                    {!known && <div>{tc("resolveOnDesktop", "Send it to the desktop; the product and unit are chosen there.")}</div>}
                </div>
                {known && draft.already_counted && (
                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="phone-already-counted">
                        {tc("alreadyCountedQty", "Already counted: {{qty}} {{unit}}", { qty: draft.already_counted.quantity, unit: draft.already_counted.unit || unit })}
                    </div>
                )}
            </div>
            <div className="p-4">
                <label className="text-xs font-semibold text-neutral-700">{tc("quantityCounted", "Quantity counted")}</label>
                <div className="mt-1.5 flex items-stretch rounded-lg border border-neutral-300 bg-white overflow-hidden focus-within:border-emerald-700">
                    <input ref={qtyRef} value={quantity} onChange={(e) => onQty(e.target.value)}
                        type="number" min="0" step="any" inputMode="decimal" data-testid="phone-qty-input"
                        className="flex-1 min-w-0 h-14 px-3 text-2xl font-semibold tabular-nums text-center outline-none" />
                    <span className="px-4 grid place-items-center bg-neutral-50 border-l border-neutral-200 text-sm font-semibold text-neutral-700 min-w-[64px]"
                          data-testid="phone-qty-unit">{unit || "—"}</span>
                </div>
            </div>
        </div>
    );
}

function ResultBanner({ result }) {
    if (!result) return null;
    const qty = `${result.item?.quantity ?? result.quantity} ${result.item?.unit || ""}`.trim();
    const map = {
        saved: ["border-emerald-200 bg-emerald-50 text-emerald-900", tc("savedLine", "Saved: {{name}} · {{qty}}", { name: result.item?.product_name || result.product_name, qty })],
        pending: ["border-amber-200 bg-amber-50 text-amber-900", tc("pendingLine", "Waiting for desktop: {{barcode}} × {{qty}} — not saved yet", { barcode: result.barcode, qty: result.quantity })],
        processing: ["border-amber-200 bg-amber-50 text-amber-900", tc("processingLine", "Still saving — check Counted items before trying again")],
        already_counted: ["border-amber-200 bg-amber-50 text-amber-900", tc("alreadyCountedChoose", "Already counted — choose Replace or Add, or Next scan")],
        conflict: ["border-amber-200 bg-amber-50 text-amber-900", tc("changedElsewhere", "Changed on another device: now {{qty}} {{unit}} — choose Replace or Add again", { qty: result.duplicate?.current_quantity, unit: result.duplicate?.unit || "" })],
        failed: ["border-red-200 bg-red-50 text-red-800", `${tc("notSaved", "Not saved")}: ${result.error || ""}`],
    };
    const [cls, text] = map[result.status] || map.failed;
    return <div className={`rounded-lg border px-3 py-2 text-sm ${cls}`} data-testid={`phone-result-${result.status}`}>{text}</div>;
}

function ScanActions({ draft, busy, quantity, onSubmit, onNext, onFinish }) {
    const known = draft?.matched === "internal";
    const counted = known && draft?.already_counted;
    const can = !!draft && Number(quantity) > 0 && !busy;
    const primary = "h-12 rounded-lg bg-[#24423a] text-white text-sm font-semibold disabled:opacity-40";
    const secondary = "h-11 rounded-lg border border-neutral-300 bg-white text-sm font-semibold disabled:opacity-40";
    return (
        <div className="space-y-2">
            {counted ? (
                <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => onSubmit("replace")} disabled={!can} data-testid="phone-replace-btn" className={primary}>
                        {i18n.t("ui.companion.replace", "Replace")}
                    </button>
                    <button onClick={() => onSubmit("add")} disabled={!can} data-testid="phone-add-btn"
                        className="h-12 rounded-lg border border-[#24423a] text-[#24423a] bg-white text-sm font-semibold disabled:opacity-40">
                        {i18n.t("common.add", "Add")}
                    </button>
                </div>
            ) : (
                <button onClick={() => onSubmit(null)} disabled={!can} data-testid="phone-save-btn" className={`w-full ${primary}`}>
                    {busy ? tc("saving", "Saving…") : (known || !draft) ? tc("saveCount", "Save count") : tc("sendToDesktop", "Send to desktop")}
                </button>
            )}
            <div className="grid grid-cols-2 gap-2">
                <button onClick={onNext} disabled={busy} data-testid="phone-next-btn" className={secondary}>
                    {i18n.t("ui.companion.nextScan", "Next Scan")}
                </button>
                <button onClick={onFinish} data-testid="phone-finish-btn" className={secondary}>
                    {i18n.t("ui.companion.finish", "Finish")}
                </button>
            </div>
        </div>
    );
}

function CountedList({ list, onCorrect }) {
    const { items, scans } = list;
    return (
        <section className="pt-2" data-testid="phone-counted-list">
            <div className="flex items-baseline justify-between">
                <h2 className="text-base font-semibold">{tc("countedItems", "Counted items")}</h2>
                <span className="text-xs text-neutral-500" data-testid="phone-counted-total">{tc("productsN", "{{n}} products", { n: items.length })}</span>
            </div>
            <ul className="mt-2 divide-y divide-neutral-200 border-y border-neutral-200">
                {scans.map((s) => (
                    <li key={s.id} className="py-2.5 flex items-center gap-3" data-testid={`phone-scan-${s.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono truncate">{s.barcode}</div>
                            <div className="text-xs text-neutral-500">× {s.quantity}</div>
                        </div>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.resolution_status === "failed" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800"}`}>
                            {s.resolution_status === "failed" ? tc("failed", "Failed") : tc("waitingDesktop", "Waiting for desktop")}
                        </span>
                    </li>
                ))}
                {items.map((it) => (
                    <li key={it.id} className="py-2.5 flex items-center gap-3" data-testid={`phone-item-${it.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{it.product_name}</div>
                            <div className="text-xs text-neutral-600 tabular-nums" data-testid={`phone-item-qty-${it.id}`}>{it.quantity} {it.unit}</div>
                        </div>
                        {it.missing_price && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800" data-testid={`phone-item-noprice-${it.id}`}>
                                {tc("priceMissing", "Price missing")}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">
                            <Check className="h-3 w-3" /> {tc("saved", "Saved")}
                        </span>
                        {it.barcode && (
                            <button onClick={() => onCorrect(it)} aria-label={tc("correct", "Correct")} data-testid={`phone-item-correct-${it.id}`}
                                className="h-8 w-8 grid place-items-center rounded-md border border-neutral-200 text-neutral-600">
                                <Pencil className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </li>
                ))}
                {!items.length && !scans.length && (
                    <li className="py-4 text-sm text-neutral-500 text-center">{tc("nothingCountedYet", "Nothing counted yet")}</li>
                )}
            </ul>
        </section>
    );
}

function BigButton({ icon, label, onClick, testId }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className="h-24 rounded-md border border-neutral-300 bg-white flex flex-col items-center justify-center gap-1 text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100">
            {icon}
            <span className="text-xs font-semibold">{label}</span>
        </button>
    );
}
